/**
 * Shared auth form styles – matches TrainingSessions form design for consistency
 * across SignUp, SignUpComplete, and GoogleCompleteSignup. Mobile-friendly (16px inputs).
 */
import styled, { css } from 'styled-components';

const inputSelectBase = css`
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e0e6ed;
  font-family: 'Poppins', sans-serif;
  background: #fff;
  box-sizing: border-box;
  color: var(--text-gray);
  appearance: none;
  -webkit-appearance: none;
  min-height: 2.5rem;
  line-height: 1.5;
  transition: var(--transition-normal);

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.2);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  @media (max-width: 768px) {
    font-size: 16px !important;
    padding: 0.5rem 0.7rem;
    min-height: 2.75rem;
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
    -webkit-text-fill-color: var(--text-gray);
  }
`;

export const AuthFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: left;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }
`;

export const AuthLabel = styled.label`
  display: block;
  font-size: 0.8rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.2rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

export const AuthInput = styled.input`
  ${inputSelectBase}

  &::placeholder {
    font-size: 0.95rem;
  }
`;

export const AuthSelect = styled.select`
  ${inputSelectBase}
  cursor: pointer;

  @media (max-width: 768px) {
    text-align: center;
    text-align-last: center;
  }

  option {
    background: #fff;
    color: var(--text-gray);
  }
`;

/** Full-width prominent field (e.g. Athlete ID) – larger, more visible */
export const AuthInputProminent = styled(AuthInput)`
  min-height: 3rem;
  font-size: 1.05rem;
  padding: 0.75rem 1rem;
  border-width: 2px;
  border-color: var(--accent-orange);
  background: rgba(255, 150, 0, 0.04);

  @media (max-width: 768px) {
    font-size: 16px !important;
    min-height: 3.25rem;
    padding: 0.65rem 1rem;
  }
`;

export const AuthLabelProminent = styled(AuthLabel)`
  font-size: 0.95rem;
  text-transform: none;
  letter-spacing: 0;
  color: var(--accent-orange);
  margin-bottom: 0.35rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

export const AuthRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

export const AuthForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;

  @media (max-width: 768px) {
    gap: 1.25rem;
  }
`;

export const AuthButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 2px solid var(--accent-orange);
  background: var(--accent-orange);
  color: var(--text-light);
  font-size: 0.75rem;
  font-weight: 800;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.5rem;
  min-width: 120px;
  white-space: nowrap;
  min-height: 2.75rem;

  &:hover {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-height: 3rem;
    font-size: 0.8rem;
  }
`;

export const AuthErrorMessage = styled.div`
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: var(--radius-md);
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
`;

export const AuthHint = styled.p`
  font-size: 0.75rem;
  color: var(--text-gray);
  opacity: 0.85;
  margin-top: 0.35rem;
  margin-bottom: 0;
  font-family: 'Poppins', sans-serif;
  line-height: 1.35;
`;

export const AuthCheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  text-align: left;
  margin-top: 0.25rem;
`;

export const AuthCheckbox = styled.input.attrs({ type: 'checkbox' })`
  margin-top: 0.25rem;
  width: 1.1rem;
  height: 1.1rem;
  accent-color: var(--accent-orange);
  cursor: pointer;
  flex-shrink: 0;
`;

export const AuthCheckboxLabel = styled.label`
  font-size: 0.85rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  line-height: 1.4;
  a {
    color: var(--accent-orange);
    text-decoration: underline;
    font-weight: 600;
  }
`;
